const mapCache = new WeakMap();

const getLocalVariants = (picker) => {
  const script = picker.querySelector('script[data-local-variant-map]');
  if (!(script instanceof HTMLScriptElement) || !script.textContent) return null;

  const text = script.textContent;
  const cached = mapCache.get(picker);
  if (cached?.script === script && cached?.text === text) return cached.variants;

  try {
    const parsed = JSON.parse(text);
    const variants = Array.isArray(parsed) ? parsed : null;
    mapCache.set(picker, { script, text, variants });
    return variants;
  } catch (error) {
    mapCache.set(picker, { script, text, variants: null });
    console.warn('[local-variant-sync] Failed to parse local variant map:', error);
    return null;
  }
};

const getSelectedConstraints = (picker) => {
  const selected = new Map();

  for (const select of picker.querySelectorAll('select.variant-option__select')) {
    const option = select.selectedOptions?.[0];
    const position = Number(option?.dataset.inputId?.split('-')?.[0]);
    if (position > 0 && option) selected.set(position - 1, option.value);
  }

  for (const input of picker.querySelectorAll('fieldset input[type="radio"][data-input-id]:checked')) {
    const position = Number(input.dataset.inputId?.split('-')?.[0]);
    if (position > 0) selected.set(position - 1, input.value);
  }

  return selected;
};

const resolveLocalVariant = (picker) => {
  const variants = getLocalVariants(picker);
  const selected = getSelectedConstraints(picker);
  if (!variants?.length || selected.size === 0) return null;

  const matches = variants.filter((variant) => {
    if (!Array.isArray(variant?.options)) return false;
    for (const [index, value] of selected) {
      if (variant.options[index] !== value) return false;
    }
    return true;
  });

  return matches.length === 1 ? matches[0] : null;
};

const findProductForm = (picker) => {
  const context = picker.closest('product-card') ?? picker.closest('dialog') ?? picker.closest('.shopify-section');
  if (!context) return null;

  const forms = Array.from(context.querySelectorAll('product-form-component'));
  const productId = String(picker.dataset.productId || '');
  const matching = forms.find((form) => String(form.dataset.productId || '') === productId);
  return matching ?? (forms.length === 1 ? forms[0] : null);
};

const syncFormVariant = (picker, variant) => {
  if (!variant?.id) return;

  const form = findProductForm(picker);
  if (!(form instanceof HTMLElement)) return;

  const input = form.querySelector('input[name="id"][ref="variantId"], input[name="id"]');
  const nextId = String(variant.id);

  if (input instanceof HTMLInputElement && input.value !== nextId) {
    input.value = nextId;
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  const checkout = form.querySelector('[ref="acceleratedCheckoutButtonContainer"]');
  if (checkout instanceof HTMLElement) {
    checkout.toggleAttribute('hidden', variant.available === false);
    checkout.removeAttribute('aria-busy');
    checkout.removeAttribute('data-variant-sync-lock');
    checkout.style.removeProperty('pointer-events');
    checkout.inert = false;
  }
};

const syncProductUrl = (picker, variant) => {
  if (picker.dataset.templateProductMatch !== 'true') return;
  if (picker.closest('product-card') || picker.closest('dialog')) return;
  if (!variant?.id) return;

  queueMicrotask(() => {
    const url = new URL(window.location.href);
    url.searchParams.set('variant', String(variant.id));
    window.history.replaceState({}, '', url.toString());
  });
};

if (!window.__localVariantSyncBound) {
  window.__localVariantSyncBound = true;

  document.addEventListener(
    'change',
    (event) => {
      const target = event.target instanceof Element ? event.target : null;
      const picker = target?.closest('variant-picker, swatches-variant-picker-component');
      if (!(picker instanceof HTMLElement)) return;

      const variant = resolveLocalVariant(picker);
      if (!variant) return;

      syncFormVariant(picker, variant);
      syncProductUrl(picker, variant);
    },
    true
  );
}
